/**
 * Maintenance-aware copy for failed API calls (MILO-482).
 *
 * While `milo-api` deploys, Laravel answers every request from maintenance mode:
 * HTTP 503 with the technical body `{"message": "Service Unavailable"}`. That
 * string is meaningless to a user — and it is not an error they caused — so the
 * app shows a "we're working on it, try again shortly" notice instead.
 */

export const MAINTENANCE_STATUS = 503;

/**
 * Recognises maintenance by status code only. A proxy in front of the API
 * (nginx, Cloudflare) answers 503 as well, but with its own body — usually HTML —
 * so the message text is not something to match on.
 */
export function isMaintenanceError(error: unknown): boolean {
    return (error as { response?: { status?: number } } | null | undefined)?.response?.status === MAINTENANCE_STATUS;
}

/*
 * Rate limiting. Since milo-api 1.45.0 every authenticated request on the `api`
 * group counts towards a per-user limit of 120 a minute, on top of the throttles
 * single routes already had. A 429 is a "not now", never a "not you": it says
 * nothing about the session and nothing about the request itself.
 */

export const RATE_LIMIT_STATUS = 429;

/** The `code` milo-api gives a refusal from one of its throttles on `api/*`. */
export const RATE_LIMITED_CODE = 'rate_limited';

/**
 * The `code` of milo-api's per-user lockout after too many wrong two-factor
 * codes. Its message says how long the wait is, in the user's language.
 */
export const TWO_FACTOR_LOCKED_CODE = 'two_factor_locked';

type ErrorResponseShape = {
    response?: { status?: number; data?: { message?: unknown; code?: unknown }; headers?: Record<string, unknown> };
};

function responseOf(error: unknown): ErrorResponseShape['response'] {
    return (error as ErrorResponseShape | null | undefined)?.response;
}

/**
 * Any 429: one of milo-api's throttles, a daily AI quota, or a proxy in front
 * of the API. Whatever refused, it says nothing about the session.
 */
export function isRateLimitError(error: unknown): boolean {
    return responseOf(error)?.status === RATE_LIMIT_STATUS;
}

/**
 * A 429 from one of milo-api's throttles on `api/*`: the per-user limiter on the
 * whole group (120 requests a minute) or a route's own. The API marks these with
 * `code: 'rate_limited'`; the web routes (sign-in, public documents) answer
 * without a code, and the daily AI quotas carry a code of their own.
 */
export function isThrottleError(error: unknown): boolean {
    const response = responseOf(error);

    return response?.status === RATE_LIMIT_STATUS && response.data?.code === RATE_LIMITED_CODE;
}

/**
 * How long a 429 asks the client to wait, in milliseconds, from its
 * `Retry-After` header: delta-seconds (what Laravel sends) or an HTTP date.
 * `null` when the header is absent or unreadable. milo-api lists the header in
 * its CORS `exposed_headers`; without that the browser would hide it.
 */
export function retryAfterMs(error: unknown): number | null {
    const raw = responseOf(error)?.headers?.['retry-after'];
    const value = typeof raw === 'number' ? String(raw) : typeof raw === 'string' ? raw.trim() : '';

    if (/^\d+$/.test(value)) {
        return Number(value) * 1000;
    }

    const date = value ? Date.parse(value) : Number.NaN;

    return Number.isNaN(date) ? null : Math.max(0, date - Date.now());
}

/**
 * The message to show for a failed API call: the maintenance notice for a 503,
 * the rate-limit notice for a throttled request, the backend's own message for
 * any other client error (4xx), and `fallback` for everything else.
 *
 * A 5xx never carries copy meant for a user: with debug on, Laravel puts the raw
 * exception text there (a Mollie 404 complete with request body, as seen on the
 * accept-invite page), and in production a bare "Server Error". Neither belongs
 * in an alert.
 *
 * A non-string body (an HTML error page, a validation object) is never shown
 * as-is — it would surface as raw markup or "[object Object]".
 */
export function apiErrorMessage(error: unknown, t: (key: string) => string, fallback: string): string {
    if (isMaintenanceError(error)) {
        return t('error.maintenance');
    }

    // A throttle's message is written for API integrators (it points at the
    // Retry-After header). Other 429s keep theirs: a daily AI quota or the quote
    // signing code limit explains itself in the user's language.
    if (isThrottleError(error)) {
        return t('error.tooManyRequests');
    }

    const response = responseOf(error);

    if (!isClientError(response?.status)) {
        return fallback;
    }

    const message = response?.data?.message;

    return typeof message === 'string' && message.trim() ? message : fallback;
}

/** A 4xx is the backend answering the request itself, so its message is meant for the user. */
function isClientError(status: number | undefined): boolean {
    return status !== undefined && status >= 400 && status < 500;
}
